import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Heading,
  useToast
} from '@homebotapp/hb-react-component-catalog'
import { useEffect, useState, useMemo } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { NEW_STEPS, OnboardingStep } from '../../../../constants/client/onboarding'
import {
  selectAnswerCityCenter,
  selectClientId,
  selectClientIsBuyer,
  selectClientIsLoadingData
} from '../../../../store/selectors/client'
import { selectCustomMailerVideoId } from '../../../../store/selectors/customMailer'
import { EnterCloseDate } from '../components/EnterCloseDate'
import { EnterGoal } from '../components/EnterGoal'
import { selectCustomerId } from '../../../../store/selectors/customerProfile'
import { EnterBudget } from './EnterBudget'
import { useEffectOncePerSessionConditionally } from '../../../../hooks'
import { enableHBB } from '../../../../api/mikasa/requests'
import { useGetClientId } from '../../../../hooks/useGetClientId'
import { fetchBuyerInfoData } from '../../../../actions'
import { BuyerInfo } from '../../../../types/buyerInfo'
import { selectBuyerInfo } from '../../../../store/selectors/buyerInfo'
import { UPDATE_BUYER_INFO_SUCCESS } from '../../../../actions/actionTypes'
import { fetchClientData } from '../../../../actions/client'

const MSG = defineMessages({
  stepXofY: {
    id: 'BuyerOnboardingWizard.step',
    defaultMessage: 'Step {step} of {total}'
  },
  yourSearchProfile: {
    id: 'BuyerOnboardingWizard.yourSearchProfile',
    defaultMessage: 'Your Search Profile'
  }
})

interface WizardModalProps {
  isShowing: boolean
  setIsShowing: (isShowing: boolean) => void
  buyerHasSetGoals: boolean
  buyerHasSetCloseDate: boolean
  buyerHasSetPricePoint: boolean
  currentStep: OnboardingStep
  setCurrentStep: (step: OnboardingStep) => void
  getInitialStep: () => OnboardingStep
  numStepsRemainingUponOpeningModal?: number
  hasFavoriteMarkets: boolean
}

const WizardModal = ({
  isShowing,
  setIsShowing,
  buyerHasSetGoals,
  buyerHasSetCloseDate,
  buyerHasSetPricePoint,
  currentStep,
  setCurrentStep,
  getInitialStep,
  numStepsRemainingUponOpeningModal,
  hasFavoriteMarkets
}: WizardModalProps) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const latLng = useSelector(selectAnswerCityCenter)
  const clientId = useSelector(selectClientId)
  const customerProfileId = useSelector(selectCustomerId)
  const customMailerVideoId = useSelector(selectCustomMailerVideoId)

  // Navigation helper functions
  const determineNextStep = () => {
    if (currentStep === NEW_STEPS.BUYER_GOALS) {
      if (!buyerHasSetCloseDate) {
        return NEW_STEPS.BUYER_CLOSE_DATE
      }
      if (!buyerHasSetPricePoint) {
        return NEW_STEPS.BUYER_ONBOARDED
      }
    } else if (currentStep === NEW_STEPS.BUYER_CLOSE_DATE) {
      if (!buyerHasSetPricePoint) {
        return NEW_STEPS.BUYER_ONBOARDED
      }
    }

    return null
  }

  const handleNavigation = (saveCurrentStepData?: () => void) => {
    // Save data if provided
    if (saveCurrentStepData) {
      saveCurrentStepData()
    }

    // Determine next step
    const nextStep = determineNextStep()

    if (nextStep) {
      setCurrentStep(nextStep)
    } else {
      // No more steps, close modal and reset
      setCurrentStep(getInitialStep())
      setIsShowing(false)
    }
  }

  const getTotalStepsLeftToComplete = () => {
    const totalStepsLeftToComplete = []
    if (!buyerHasSetGoals) {
      totalStepsLeftToComplete.push(NEW_STEPS.BUYER_GOALS)
    }

    if (!buyerHasSetCloseDate) {
      totalStepsLeftToComplete.push(NEW_STEPS.BUYER_CLOSE_DATE)
    }

    if (!buyerHasSetPricePoint) {
      totalStepsLeftToComplete.push(NEW_STEPS.BUYER_ONBOARDED)
    }

    return totalStepsLeftToComplete.length
  }

  const getStepNumber = () => {
    if (currentStep === NEW_STEPS.BUYER_GOALS) {
      return 1
    }

    if (currentStep === NEW_STEPS.BUYER_CLOSE_DATE) {
      if (buyerHasSetGoals) {
        return 1
      } else {
        return 2
      }
    }

    if (currentStep === NEW_STEPS.BUYER_ONBOARDED) {
      if (buyerHasSetGoals && buyerHasSetCloseDate) {
        return 1
      } else if ((buyerHasSetGoals && !buyerHasSetCloseDate) || (buyerHasSetCloseDate && !buyerHasSetGoals)) {
        // if just one of the other steps has been completed
        return 2
      } else {
        return 3
      }
    }

    return 3
  }

  return (
    <Modal
      size='lg'
      onOverlayClick={() => null}
      isOpen={isShowing}
      onClose={() => {
        setCurrentStep(getInitialStep())
        setIsShowing(false)
      }}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Heading textAlign='left'>{intl.formatMessage(MSG.yourSearchProfile)}</Heading>
          {numStepsRemainingUponOpeningModal && (
            <Heading textAlign='left' variant='eyebrow' size='xs' color='brand.700'>
              {intl.formatMessage(MSG.stepXofY, {
                step: getStepNumber(),
                total: numStepsRemainingUponOpeningModal
              })}
            </Heading>
          )}
        </ModalHeader>
        <ModalCloseButton
          onClick={() => {
            setCurrentStep(getInitialStep())
            setIsShowing(false)
          }}
        />
        <ModalBody paddingBottom={10}>
          {/* @ts-ignore */}
          {currentStep === NEW_STEPS.BUYER_GOALS && (
            <EnterGoal
              hasFavoriteMarkets={hasFavoriteMarkets}
              getTotalStepsLeftToComplete={getTotalStepsLeftToComplete}
              handleNavigation={handleNavigation}
            />
          )}
          {currentStep === NEW_STEPS.BUYER_CLOSE_DATE && (
            <EnterCloseDate
              hasFavoriteMarkets={hasFavoriteMarkets}
              getTotalStepsLeftToComplete={getTotalStepsLeftToComplete}
              handleNavigation={handleNavigation}
            />
          )}
          {currentStep === NEW_STEPS.BUYER_ONBOARDED && (
            <EnterBudget
              hasFavoriteMarkets={hasFavoriteMarkets}
              getTotalStepsLeftToComplete={getTotalStepsLeftToComplete}
              handleNavigation={handleNavigation}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

interface BuyerOnboardingWizardV2Props {
  isShowing: boolean
  setIsShowing: (isShowing: boolean) => void
  buyerHasSetGoals: boolean
  buyerHasSetCloseDate: boolean
  buyerHasSetPricePoint: boolean
  hasFavoriteMarkets: boolean
}

export const BuyerOnboardingWizardV2 = ({
  isShowing,
  setIsShowing,
  buyerHasSetGoals,
  buyerHasSetCloseDate,
  buyerHasSetPricePoint,
  hasFavoriteMarkets
}: BuyerOnboardingWizardV2Props) => {
  const dispatch = useDispatch()
  const clientId = useGetClientId()
  const toast = useToast()
  const intl = useIntl()

  const buyerInfo: BuyerInfo = useSelector(selectBuyerInfo)
  // initial step is based on what fields have been completed by user
  // current step begins the same as initial step but is based on where the user navigates with the next/skip buttons
  const getInitialStep = () => {
    if (!buyerHasSetGoals) {
      return NEW_STEPS.BUYER_GOALS
    }

    if (!buyerHasSetCloseDate) {
      return NEW_STEPS.BUYER_CLOSE_DATE
    }

    if (!buyerHasSetPricePoint) {
      return NEW_STEPS.BUYER_ONBOARDED
    }

    return NEW_STEPS.BUYER_GOALS
  }

  const [currentStep, setCurrentStep] = useState(null)
  const isLoading = useSelector(selectClientIsLoadingData)
  useEffect(() => {
    // if currentStep is null, can assume they just opened the modal for the first time
    // const dataLoading = !buyerHasSetGoals && !buyerHasSetCloseDate && !buyerHasSetPricePoint
    if (currentStep === null && !isLoading) {
      // THIS DOESN'T WORK because buyerHasSetGoals, buyerHasSetCloseDate, buyerHasSetPricePoint are not updated immediately
      setCurrentStep(getInitialStep())
    }
  }, [buyerHasSetGoals, buyerHasSetCloseDate, buyerHasSetPricePoint, isLoading])

  useEffectOncePerSessionConditionally(
    () => {
      enableHBB(clientId, {}).then(() => {
        dispatch(fetchBuyerInfoData(clientId))
        dispatch(fetchClientData(clientId))
      })
    },
    undefined,
    isShowing
  )

  const numStepsRemainingUponOpeningModal = useMemo(() => {
    let stepsRemaining = 0
    if (!isShowing) {
      return undefined
    }
    if (!buyerHasSetGoals) {
      stepsRemaining++
    }
    if (!buyerHasSetCloseDate) {
      stepsRemaining++
    }
    if (!buyerHasSetPricePoint) {
      stepsRemaining++
    }
    return stepsRemaining
  }, [buyerHasSetGoals, buyerHasSetCloseDate, buyerHasSetPricePoint, isShowing])

  return (
    <WizardModal
      isShowing={isShowing}
      setIsShowing={setIsShowing}
      buyerHasSetGoals={buyerHasSetGoals}
      buyerHasSetCloseDate={buyerHasSetCloseDate}
      buyerHasSetPricePoint={buyerHasSetPricePoint}
      hasFavoriteMarkets={hasFavoriteMarkets}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      getInitialStep={getInitialStep}
      numStepsRemainingUponOpeningModal={numStepsRemainingUponOpeningModal}
    />
  )
}
